import { logError } from '@/utils/logger';
import SearchableSelect from '@/components/SearchableSelect';
import { useState, useEffect, useCallback } from 'react';
import { useTranslation } from 'react-i18next';
import { Users, UserCircleGear, CalendarCheck, FileText, CheckCircle, Package, Funnel, Warning, Bell, ShoppingCart, CalendarX, MapPin } from '@phosphor-icons/react';
import { useNavigate } from 'react-router-dom';
import { BarChart, Bar, XAxis, YAxis, CartesianGrid, Tooltip, ResponsiveContainer, PieChart, Pie, Cell } from 'recharts';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/components/ui/select';
import { Badge } from '@/components/ui/badge';
import { Button } from '@/components/ui/button';
import { toast } from 'sonner';

const Dashboard = () => {
  const { t } = useTranslation();
  const navigate = useNavigate();
  const [stats, setStats] = useState(null);
  const [zones, setZones] = useState([]);
  const [purchaseNeeds, setPurchaseNeeds] = useState([]);
  const [lowStockCount, setLowStockCount] = useState(0);
  const [zoneStats, setZoneStats] = useState([]);

  const fetchNeedsList = useCallback(async () => {
    try {
      const res = await fetch(`${process.env.REACT_APP_BACKEND_URL}/api/purchase-needs`, { credentials: 'include' });
      if (res.ok) {
        const data = await res.json();
        setPurchaseNeeds(data.filter(n => n.status === 'pending'));
      }
    } catch (error) { logError('Fetch needs:', error); }
    try {
      const r = await fetch(`${process.env.REACT_APP_BACKEND_URL}/api/logistics`, { credentials: 'include' });
      if (r.ok) {
        const items = await r.json();
        setLowStockCount(items.filter((x) => (x.quantity_available ?? 0) <= 5).length);
      }
    } catch (error) { logError('Fetch stock:', error); }
    try {
      const r = await fetch(`${process.env.REACT_APP_BACKEND_URL}/api/stats/by-zone`, { credentials: 'include' });
      if (r.ok) setZoneStats(await r.json());
    } catch (error) { logError('Fetch zone stats:', error); }
  }, []);

  useEffect(() => { fetchNeedsList(); }, [fetchNeedsList]);
  const [selectedZone, setSelectedZone] = useState('all');
  const [userPermissions, setUserPermissions] = useState(null);
  const [loading, setLoading] = useState(true);
  const [contractAlerts, setContractAlerts] = useState([]);

  // Define useCallback functions BEFORE useEffect that uses them
  const fetchStats = useCallback(async () => {
    try {
      const url = selectedZone && selectedZone !== 'all'
        ? `${process.env.REACT_APP_BACKEND_URL}/api/stats/dashboard?zone_id=${selectedZone}`
        : `${process.env.REACT_APP_BACKEND_URL}/api/stats/dashboard`;
      
      const response = await fetch(url, { credentials: 'include' });
      if (response.ok) {
        const data = await response.json();
        setStats(data);
      }
    } catch (error) {
      logError('Failed to fetch stats:', error);
    }
  }, [selectedZone]);

  const fetchContractAlerts = useCallback(async () => {
    try {
      const zoneParam = selectedZone && selectedZone !== 'all' ? `?zone_id=${selectedZone}` : '';
      const response = await fetch(
        `${process.env.REACT_APP_BACKEND_URL}/api/alerts/contracts${zoneParam}`,
        { credentials: 'include' }
      );
      if (response.ok) {
        setContractAlerts(await response.json());
      }
    } catch (error) {
      logError('Failed to fetch alerts:', error);
    }
  }, [selectedZone]);

  const fetchInitialData = useCallback(async () => {
    try {
      const [userRes, zonesRes] = await Promise.all([
        fetch(`${process.env.REACT_APP_BACKEND_URL}/api/auth/me`, { credentials: 'include' }),
        fetch(`${process.env.REACT_APP_BACKEND_URL}/api/zones`, { credentials: 'include' }),
      ]);

      if (userRes.ok) {
        const userData = await userRes.json();
        setUserPermissions({...userData.permissions, effective: userData.effective_permissions || {}});
        if (!userData.permissions.can_view_all_zones && userData.zone_id) {
          setSelectedZone(userData.zone_id);
        }
      }

      if (zonesRes.ok) {
        const zonesData = await zonesRes.json();
        setZones(zonesData);
      }
    } catch (error) {
      logError('Failed to fetch initial data:', error);
    } finally {
      setLoading(false);
    }
  }, []);

  // Initial data fetch
  useEffect(() => {
    fetchInitialData();
  }, [fetchInitialData]);

  // Fetch stats and alerts when zones are loaded or selectedZone changes
  useEffect(() => {
    if (zones.length > 0) {
      fetchStats();
      fetchContractAlerts();
    }
  }, [zones.length, selectedZone, fetchStats, fetchContractAlerts]);

  const sendReminders = async () => {
    try {
      const response = await fetch(
        `${process.env.REACT_APP_BACKEND_URL}/api/alerts/send-reminders`,
        { method: 'POST', credentials: 'include' }
      );
      if (response.ok) {
        const result = await response.json();
        if (result.sent > 0) {
          toast.success(`Email envoyé avec ${result.alerts_count} alerte(s)`);
        } else {
          toast.info(result.message || 'Aucun email envoyé');
        }
      }
    } catch (error) {
      toast.error('Erreur lors de l\'envoi');
    }
  };

  const getZoneName = (zoneId) => {
    const zone = zones.find(z => z.zone_id === zoneId);
    return zone ? zone.name : zoneId;
  };

  const CHART_TOOLTIP_STYLE = {
    backgroundColor: '#0F172A',
    border: '1px solid #1E293B',
    borderRadius: '6px',
    color: '#F8FAFC',
  };

  const getAlertStyle = (urgency) => {
    if (urgency === 'critical') return 'bg-[#EF4444]/10 border border-[#EF4444]/30';
    if (urgency === 'warning') return 'bg-[#F59E0B]/10 border border-[#F59E0B]/30';
    return 'bg-[#1E293B]/50 border border-[#334155]';
  };

  const getAlertBadgeStyle = (urgency) => {
    if (urgency === 'critical') return 'bg-[#EF4444]/20 text-[#EF4444] border border-[#EF4444]/30';
    if (urgency === 'warning') return 'bg-[#F59E0B]/20 text-[#F59E0B] border border-[#F59E0B]/30';
    return 'bg-[#2563EB]/20 text-[#2563EB] border border-[#2563EB]/30';
  };

  const statCards = [
    {
      title: t('dashboard.totalClients'),
      value: stats?.total_clients || 0,
      icon: Users,
      color: '#2563EB',
      bgColor: '#2563EB20',
    },
    {
      title: t('dashboard.totalAgents'),
      value: stats?.total_agents || 0,
      icon: UserCircleGear,
      color: '#10B981',
      bgColor: '#10B98120',
    },
    {
      title: t('dashboard.activeAssignments'),
      value: stats?.active_assignments || 0,
      icon: CalendarCheck,
      color: '#F59E0B',
      bgColor: '#F59E0B20',
    },
    {
      title: t('dashboard.totalReports'),
      value: stats?.total_reports || 0,
      icon: FileText,
      color: '#8B5CF6',
      bgColor: '#8B5CF620',
    },
    {
      title: t('dashboard.availableAgents'),
      value: stats?.available_agents || 0,
      icon: CheckCircle,
      color: '#06B6D4',
      bgColor: '#06B6D420',
    },
    {
      title: 'Stock Logistique',
      value: stats?.total_logistics || 0,
      icon: Package,
      color: '#EC4899',
      bgColor: '#EC489920',
    },
  ];

  // Mock data for charts
  const monthlyData = [
    { month: 'Jan', rapports: 12, affectations: 45 },
    { month: 'Fév', rapports: 19, affectations: 52 },
    { month: 'Mar', rapports: 15, affectations: 48 },
    { month: 'Avr', rapports: 22, affectations: 58 },
    { month: 'Mai', rapports: 18, affectations: 55 },
    { month: 'Juin', rapports: 25, affectations: 62 },
  ];

  const agentStatus = [
    { name: 'Disponibles', value: stats?.available_agents || 0, color: '#10B981' },
    { name: 'Affectés', value: stats?.active_assignments || 0, color: '#2563EB' },
    { name: 'Indisponibles', value: Math.max(0, (stats?.total_agents || 0) - (stats?.available_agents || 0) - (stats?.active_assignments || 0)), color: '#94A3B8' },
  ];

  if (loading) {
    return (
      <div className="flex items-center justify-center h-full">
        <div className="text-[#94A3B8] text-lg">{t('common.loading')}</div>
      </div>
    );
  }

  return (
    <div className="space-y-6" data-testid="dashboard-page">
      {/* Page Title with Zone Filter */}
      <div className="flex flex-col sm:flex-row sm:items-center justify-between gap-4">
        <div>
          <h1 className="text-2xl sm:text-4xl font-bold text-[#F8FAFC] tracking-tight">{t('dashboard.title')}</h1>
          <p className="text-[#94A3B8] mt-1 sm:mt-2 text-sm sm:text-base">Vue d'ensemble des opérations de sécurité</p>
        </div>
        <div className="flex items-center gap-3">
          {selectedZone && selectedZone !== 'all' && (
            <Badge className="bg-[#2563EB]/20 text-[#2563EB] border border-[#2563EB]/30 text-sm py-1 px-3">
              {getZoneName(selectedZone)}
            </Badge>
          )}
          {userPermissions?.can_view_all_zones && (
            <div className="w-[180px]">
              <SearchableSelect value={selectedZone} onValueChange={setSelectedZone} options={[{ value: 'all', label: 'Toutes les zones' }, ...zones.map(z => ({ value: z.zone_id, label: z.name }))]} placeholder="Toutes les zones" testId="zone-filter" />
            </div>
          )}
        </div>
      </div>

      {/* Widget Alertes — accès rapide au calendrier */}
      {(contractAlerts.length > 0 || lowStockCount > 0) && (
        <button
          onClick={() => navigate('/alerts-calendar')}
          className="w-full bg-gradient-to-r from-[#EF4444]/10 to-[#F59E0B]/10 border border-[#F59E0B]/40 rounded-md p-4 flex items-center justify-between gap-4 hover:from-[#EF4444]/20 hover:to-[#F59E0B]/20 transition-colors group"
          data-testid="alerts-widget"
        >
          <div className="flex items-center gap-4">
            <div className="h-11 w-11 rounded-md bg-[#F59E0B]/20 flex items-center justify-center flex-shrink-0">
              <Warning size={22} weight="duotone" className="text-[#F59E0B] group-hover:animate-pulse" />
            </div>
            <div className="text-left">
              <p className="text-sm sm:text-base font-semibold text-[#F8FAFC]">
                {contractAlerts.length + lowStockCount} alerte{contractAlerts.length + lowStockCount > 1 ? 's' : ''} à surveiller
              </p>
              <p className="text-xs text-[#94A3B8]">
                {contractAlerts.length} contrat{contractAlerts.length > 1 ? 's' : ''} à renouveler ·{' '}
                {lowStockCount} article{lowStockCount > 1 ? 's' : ''} en stock bas
              </p>
            </div>
          </div>
          <div className="flex items-center gap-2 text-[#F59E0B] text-sm font-medium">
            <CalendarX size={18} weight="duotone" />
            <span className="hidden sm:inline">Voir le calendrier →</span>
          </div>
        </button>
      )}

      {/* Statistiques par zone */}
      {zoneStats.length > 0 && (
        <div className="bg-[#0F172A] border border-[#1E293B] rounded-md p-4" data-testid="zone-stats-section">
          <h3 className="text-sm font-semibold text-[#F8FAFC] mb-3 flex items-center gap-2">
            <MapPin size={16} weight="duotone" className="text-[#2563EB]" /> Statistiques par zone
          </h3>
          <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-3">
            {zoneStats.map(z => (
              <div key={z.zone_id} className="p-3 rounded-md bg-[#1E293B]/50 border border-[#1E293B]" data-testid={`zone-stat-${z.zone_id}`}>
                <p className="text-[#F8FAFC] font-semibold mb-2">{z.name}</p>
                <div className="grid grid-cols-3 gap-2 text-center">
                  <div>
                    <p className="text-[10px] uppercase tracking-wider text-[#64748B]">Clients</p>
                    <p className="text-lg font-bold text-[#2563EB]">{z.clients}</p>
                  </div>
                  <div>
                    <p className="text-[10px] uppercase tracking-wider text-[#64748B]">Agents</p>
                    <p className="text-lg font-bold text-[#10B981]">{z.agents}</p>
                  </div>
                  <div>
                    <p className="text-[10px] uppercase tracking-wider text-[#64748B]">Contrats</p>
                    <p className="text-lg font-bold text-[#F59E0B]">{z.active_contracts}</p>
                  </div>
                </div>
              </div>
            ))}
          </div>
        </div>
      )}

      {/* Stats Grid */}
      <div className="grid grid-cols-2 sm:grid-cols-3 xl:grid-cols-6 gap-3 sm:gap-4">
        {statCards.map((stat) => (
          <div
            key={stat.title}
            className="bg-[#0F172A] border border-[#1E293B] rounded-md p-4 sm:p-6 hover:border-[#334155] transition-colors"
            data-testid={`stat-card-${stat.title}`}
          >
            <div className="flex items-start justify-between">
              <div>
                <p className="text-[#94A3B8] text-[10px] sm:text-xs uppercase tracking-wide mb-1 sm:mb-2">{stat.title}</p>
                <p className="text-2xl sm:text-3xl font-bold text-[#F8FAFC]">{stat.value}</p>
              </div>
              <div
                className="h-10 w-10 rounded-md flex items-center justify-center"
                style={{ backgroundColor: stat.bgColor }}
              >
                <stat.icon size={20} weight="duotone" style={{ color: stat.color }} />
              </div>
            </div>
          </div>
        ))}
      </div>

      {/* Charts Grid */}
      <div className="grid grid-cols-1 lg:grid-cols-3 gap-4 sm:gap-6">
        {/* Monthly Activity */}
        <div className="lg:col-span-2 bg-[#0F172A] border border-[#1E293B] rounded-md p-6">
          <h2 className="text-xl font-bold text-[#F8FAFC] mb-6">Activité Mensuelle</h2>
          <ResponsiveContainer width="100%" height={300}>
            <BarChart data={monthlyData}>
              <CartesianGrid strokeDasharray="3 3" stroke="#1E293B" />
              <XAxis dataKey="month" stroke="#94A3B8" />
              <YAxis stroke="#94A3B8" />
              <Tooltip contentStyle={CHART_TOOLTIP_STYLE} />
              <Bar dataKey="rapports" fill="#2563EB" name="Rapports" radius={[4, 4, 0, 0]} />
              <Bar dataKey="affectations" fill="#10B981" name="Affectations" radius={[4, 4, 0, 0]} />
            </BarChart>
          </ResponsiveContainer>
        </div>

        {/* Agent Status */}
        <div className="bg-[#0F172A] border border-[#1E293B] rounded-md p-6">
          <h2 className="text-xl font-bold text-[#F8FAFC] mb-6">État des Agents</h2>
          <ResponsiveContainer width="100%" height={200}>
            <PieChart>
              <Pie
                data={agentStatus}
                cx="50%"
                cy="50%"
                innerRadius={50}
                outerRadius={80}
                paddingAngle={5}
                dataKey="value"
              >
                {agentStatus.map((entry, index) => (
                  <Cell key={`cell-${index}`} fill={entry.color} />
                ))}
              </Pie>
              <Tooltip
                contentStyle={CHART_TOOLTIP_STYLE}
              />
            </PieChart>
          </ResponsiveContainer>
          <div className="mt-4 space-y-2">
            {agentStatus.map((item) => (
              <div key={item.name} className="flex items-center justify-between">
                <div className="flex items-center gap-2">
                  <div className="h-3 w-3 rounded-full" style={{ backgroundColor: item.color }}></div>
                  <span className="text-sm text-[#94A3B8]">{item.name}</span>
                </div>
                <span className="text-sm font-medium text-[#F8FAFC]">{item.value}</span>
              </div>
            ))}
          </div>
        </div>
      </div>

      {/* Zones Overview (Admin only) */}
      {userPermissions?.can_view_all_zones && zones.length > 0 && (
        <div className="bg-[#0F172A] border border-[#1E293B] rounded-md p-6">
          <h2 className="text-xl font-bold text-[#F8FAFC] mb-6">Bureaux / Zones</h2>          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-4">
            {zones.map(zone => (
              <div
                key={zone.zone_id}
                className="bg-[#1E293B]/50 border border-[#334155] rounded-md p-4 cursor-pointer hover:border-[#2563EB] transition-colors"
                onClick={() => setSelectedZone(zone.zone_id)}
              >
                <div className="flex items-center justify-between mb-2">
                  <h3 className="text-lg font-bold text-[#F8FAFC]">{zone.name}</h3>
                  <Badge className="bg-[#2563EB]/20 text-[#2563EB] border border-[#2563EB]/30">
                    {zone.code}
                  </Badge>
                </div>
                <p className="text-sm text-[#94A3B8]">Cliquez pour filtrer</p>
              </div>
            ))}
          </div>
        </div>
      )}

      {/* Besoins d'achat en attente */}
      {purchaseNeeds.length > 0 && (
        <div className="bg-[#0F172A] border border-[#1E293B] rounded-md p-6" data-testid="dashboard-purchase-needs">
          <div className="flex items-center gap-3 mb-4">
            <ShoppingCart size={24} weight="duotone" className="text-[#2563EB]" />
            <h2 className="text-xl font-bold text-[#F8FAFC]">Besoins d'achat en attente ({purchaseNeeds.length})</h2>
          </div>
          <div className="space-y-2">
            {[...purchaseNeeds].sort((a, b) => (b.urgent ? 1 : 0) - (a.urgent ? 1 : 0)).slice(0, 5).map(need => (
              <div key={need.need_id} className={`flex flex-col sm:flex-row sm:items-center justify-between gap-2 p-3 rounded-md border ${need.urgent ? 'bg-[#EF4444]/5 border-[#EF4444]/30' : 'bg-[#1E293B]/40 border-[#334155]/50'}`}>
                <div className="flex items-center gap-2 min-w-0">
                  {need.urgent && <Badge className="bg-[#EF4444]/20 text-[#EF4444] border border-[#EF4444]/30 text-xs flex-shrink-0">URGENT</Badge>}
                  <p className="text-sm text-[#F8FAFC] truncate">{need.description}</p>
                </div>
                <p className="text-xs text-[#94A3B8] flex-shrink-0">
                  <span className="text-[#10B981] font-semibold">{need.total?.toFixed(2)} $</span>
                  {need.due_date && ` • avant le ${new Date(need.due_date).toLocaleDateString('fr-FR')}`}
                </p>
              </div>
            ))}
          </div>
        </div>
      )}

      {/* Contract Alerts */}
      {contractAlerts.length > 0 && userPermissions?.effective?.clients?.read && (
        <div className="bg-[#0F172A] border border-[#1E293B] rounded-md p-6">
          <div className="flex items-center justify-between mb-6">
            <div className="flex items-center gap-3">
              <Warning size={24} weight="duotone" className="text-[#F59E0B]" />
              <h2 className="text-xl font-bold text-[#F8FAFC]">Alertes - Contrats à Renouveler</h2>
            </div>
            {userPermissions?.can_view_all_zones && (
              <Button 
                onClick={sendReminders}
                className="bg-[#F59E0B] hover:bg-[#D97706] text-white"
                size="sm"
              >
                <Bell size={16} className="mr-2" />
                Envoyer rappels
              </Button>
            )}
          </div>
          <div className="space-y-3">
            {contractAlerts.slice(0, 5).map(alert => (
              <div 
                key={alert.client_id}
                className={`flex items-center justify-between p-4 rounded-md ${
                  alert.urgency === 'critical' ? 'bg-[#EF4444]/10 border border-[#EF4444]/30' :
                  alert.urgency === 'warning' ? 'bg-[#F59E0B]/10 border border-[#F59E0B]/30' :
                  'bg-[#1E293B]/50 border border-[#334155]'
                }`}
              >
                <div>
                  <p className="text-[#F8FAFC] font-medium">{alert.client_name}</p>
                  <p className="text-sm text-[#94A3B8]">
                    Contrat N° {alert.contract_number || 'N/A'} - Expire le {new Date(alert.contract_end).toLocaleDateString('fr-FR')}
                  </p>
                </div>
                <Badge className={
                  alert.urgency === 'critical' ? 'bg-[#EF4444]/20 text-[#EF4444] border border-[#EF4444]/30' :
                  alert.urgency === 'warning' ? 'bg-[#F59E0B]/20 text-[#F59E0B] border border-[#F59E0B]/30' :
                  'bg-[#2563EB]/20 text-[#2563EB] border border-[#2563EB]/30'
                }>
                  {alert.days_remaining <= 0 ? 'Expiré' : `${alert.days_remaining} jours`}
                </Badge>
              </div>
            ))}
          </div>
        </div>
      )}
    </div>
  );
};

export default Dashboard;
